/**
 * Credits: shared strings, types and helpers.
 *
 * STRINGS
 * Every string the credits screen shows lives in CREDITS_STRINGS with its
 * English text, so the screen renders correctly before the locale files are
 * updated and the merger has one object to copy from. Keys are the i18n keys;
 * `{{x}}` is i18next interpolation.
 *
 * PLURALS
 * A key that depends on a count is written as a `_one` / `_other` pair and is
 * always called with `count`. `useCreditsT` hands both English forms to i18next
 * as `defaultValue_one` / `defaultValue_other`, so plural selection works even
 * when no locale resource exists yet.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../lib/api';
import { useApiError } from '../lib/useApiError';

export const CREDITS_STRINGS = {
  'credits.title': 'Credits',
  'credits.subtitle': 'Your balance, what credits buy, and every movement on your account.',

  // Balance
  'credits.balance.label': 'Balance',
  'credits.balance.heldBy': 'Held by {{organization}}',
  'credits.balance.low': 'Not enough for any paid action. Ask your provider for more credits.',
  'credits.balance.loadFailed': 'Your credit balance could not be loaded. This does not mean it is zero.',

  // Price list
  'credits.pricing.title': 'What credits buy',
  'credits.pricing.subtitle': 'The cost of each action, and how many you could do with your balance.',
  'credits.pricing.action': 'Action',
  'credits.pricing.cost': 'Cost',
  'credits.pricing.affordable': 'You can afford',
  'credits.pricing.times': '{{formatted}}×',
  'credits.pricing.frameworks': 'Framework licences',
  'credits.pricing.frameworkUnit': 'per licence, for one year',
  'credits.pricing.footnote': 'These are the standard prices. A questionnaire template that sets its own price shows that price when you send it.',
  'credits.pricing.none': 'No prices are published yet.',
  'credits.pricing.loadFailed': 'The price list could not be loaded. This does not mean everything is free.',
  'credits.cost_one': '{{amount}} credit',
  'credits.cost_other': '{{amount}} credits',
  'credits.unit.TPRM_ASSESSMENT': 'per questionnaire sent',
  'credits.unit.VULNERABILITY_SCAN': 'per scheduled run',

  // Reasons (used by the price list and the ledger)
  'credits.reason.GRANT': 'Credits granted',
  'credits.reason.FRAMEWORK_LICENSE': 'Framework licence',
  'credits.reason.TPRM_ASSESSMENT': 'Vendor questionnaire',
  'credits.reason.MARITIME_ASSESSMENT': 'Maritime assessment',
  'credits.reason.VULNERABILITY_SCAN': 'Vulnerability scan',
  'credits.reason.SELF_ASSESSMENT_SCAN': 'Self-assessment scan',
  'credits.reason.TRANSFER': 'Transfer',
  'credits.reason.FORFEIT': 'Forfeited',

  // Ledger
  'credits.ledger.title': 'Ledger',
  'credits.ledger.subtitle': 'Every credit movement on this account, newest first.',
  'credits.ledger.caption': 'Credit ledger',
  'credits.ledger.when': 'When',
  'credits.ledger.what': 'What for',
  'credits.ledger.who': 'By',
  'credits.ledger.amount': 'Credits',
  'credits.ledger.balanceAfter': 'Balance',
  'credits.ledger.balanceAfterHint': 'Balance after this entry',
  'credits.ledger.filterType': 'Type',
  'credits.ledger.filterDirection': 'Direction',
  'credits.ledger.directionIn': 'Credits in',
  'credits.ledger.directionOut': 'Credits out',
  'credits.ledger.from': 'From',
  'credits.ledger.to': 'To',
  'credits.ledger.search': 'Search',
  'credits.ledger.searchPlaceholder': 'Note, person or reference',
  'credits.ledger.rangeInverted': 'The “from” date is after the “to” date, so nothing can match.',
  'credits.ledger.totalIn': 'In',
  'credits.ledger.totalOut': 'Out',
  'credits.ledger.totalNet': 'Net',
  'credits.ledger.totalsNote_one': 'Totals for the {{count}} entry shown by the current filters.',
  'credits.ledger.totalsNote_other': 'Totals for the {{count}} entries shown by the current filters.',
  'credits.ledger.showing': 'Showing {{from}}–{{to}} of {{total}}',
  'credits.ledger.showingFiltered': 'Showing {{from}}–{{to}} of {{total}} (filtered from {{all}})',
  'credits.ledger.pagination': 'Ledger pages',
  'credits.ledger.none': 'No credit movements yet',
  'credits.ledger.noneHint': 'Credits you receive, spend or transfer will be listed here.',
  'credits.ledger.noMatch': 'No entries match these filters.',
  'credits.ledger.loadFailed': 'The ledger could not be loaded. This does not mean there were no charges.',
  'credits.ledger.partial': 'Only the most recent {{loaded}} of {{total}} entries were loaded. Filters and totals cover those entries only; running balances are still correct.',
  'credits.ledger.changed': 'The ledger changed while it was loading, so it may be incomplete. Reload to get a consistent view.',
  'credits.ledger.mismatch': 'The ledger entries add up to {{ledger}} but the balance is {{balance}}. Reload to check again; if this persists, contact support.',
  'credits.ledger.reload': 'Reload',
  'credits.ledger.system': 'System',
  'credits.ledger.unnamedUser': 'Unnamed user',
  'credits.ledger.toOrg': 'To {{name}}',
  'credits.ledger.fromOrg': 'From {{name}}',
  'credits.ledger.otherOrg': 'another organisation',
  'credits.ledger.ref': 'Ref {{id}}',
  'credits.ledger.entryId': 'Entry {{id}}',
  'credits.ledger.credited': 'credit',
  'credits.ledger.debited': 'debit',

  // Allocation
  'credits.alloc.title': 'Allocate credits',
  'credits.alloc.subtitle': 'Move credits from your balance to organisations in your channel, or take unspent credits back.',
  'credits.alloc.subtitleOperator': 'Grant credits to organisations in your channel. Granted credits are created; they do not come out of your balance.',
  'credits.alloc.search': 'Search organisations',
  'credits.alloc.none': 'No organisations in your channel yet',
  'credits.alloc.noneHint': 'Organisations you create appear here, and you can allocate credits to them.',
  'credits.alloc.noMatch': 'No organisation matches that search.',
  'credits.alloc.loadFailed': 'Your channel could not be loaded, so credits cannot be allocated right now.',
  'credits.alloc.truncated': 'Your channel is very large. Only the first {{count}} organisations are listed.',
  'credits.alloc.showMore': 'Show more',
  'credits.alloc.button': 'Allocate',
  'credits.alloc.buttonFor': 'Allocate or reclaim credits for {{name}}',
  'credits.alloc.trial': 'Trial',
  'credits.alloc.inactive': 'Deactivated',
  'credits.alloc.holds': 'Holds',
  'credits.alloc.modalTitle': 'Credits for {{name}}',
  'credits.alloc.action': 'Action',
  'credits.alloc.modeGive': 'Allocate',
  'credits.alloc.modeTake': 'Reclaim',
  'credits.alloc.modeGiveHint': 'Move credits from your balance to {{name}}.',
  'credits.alloc.modeGiveHintOperator': 'Create new credits and add them to {{name}}.',
  'credits.alloc.modeTakeHint': 'Take unspent credits back from {{name}} into your balance.',
  'credits.alloc.modeTakeHintOperator': 'Remove credits from {{name}}. They are not returned to you.',
  'credits.alloc.amount': 'Number of credits',
  'credits.alloc.amountHint': 'A whole number.',
  'credits.alloc.max': 'Max {{amount}}',
  'credits.alloc.useMax': 'Use the maximum: {{amount}}',
  'credits.alloc.note': 'Note (optional)',
  'credits.alloc.noteHint': 'Recorded in the ledger of both organisations. {{used}} of {{max}} characters.',
  'credits.alloc.preview': 'Balances after this',
  'credits.alloc.yourBalance': 'Your balance',
  'credits.alloc.unchanged': 'Unchanged',
  'credits.alloc.becomes': 'becomes',
  'credits.alloc.leavesZero': 'This leaves your organisation with no credits, so it could not send questionnaires or run scans.',
  'credits.alloc.previewEmpty': 'Enter an amount to see the resulting balances.',
  'credits.alloc.trialCap': '{{name}} is on a trial plan and can hold at most {{cap}} credits.',
  'credits.alloc.errNotWhole': 'Enter a whole number of credits, such as 25.',
  'credits.alloc.errZero': 'Enter at least 1 credit.',
  'credits.alloc.errNoCredits': 'You have no credits to allocate.',
  'credits.alloc.errExceedsSource': 'You only have {{available}} credits to allocate.',
  'credits.alloc.errExceedsCap': '{{name}} can hold at most {{cap}} credits, so you can allocate up to {{max}} more.',
  'credits.alloc.errAtCap': '{{name}} is already at its limit of {{cap}} credits.',
  'credits.alloc.errNothingToReclaim': '{{name}} has no credits to reclaim.',
  'credits.alloc.errExceedsTheirs': '{{name}} only holds {{available}} credits, which is the most you can reclaim.',
  'credits.alloc.confirmGive_one': 'Allocate {{amount}} credit',
  'credits.alloc.confirmGive_other': 'Allocate {{amount}} credits',
  'credits.alloc.confirmTake_one': 'Reclaim {{amount}} credit',
  'credits.alloc.confirmTake_other': 'Reclaim {{amount}} credits',
  'credits.alloc.working': 'Working…',
  'credits.alloc.doneGive_one': 'Allocated {{amount}} credit to {{name}}. Their balance is now {{balance}}.',
  'credits.alloc.doneGive_other': 'Allocated {{amount}} credits to {{name}}. Their balance is now {{balance}}.',
  'credits.alloc.doneTake_one': 'Reclaimed {{amount}} credit from {{name}}. Their balance is now {{balance}}.',
  'credits.alloc.doneTake_other': 'Reclaimed {{amount}} credits from {{name}}. Their balance is now {{balance}}.',
  'credits.alloc.unknownOutcome': 'We could not confirm whether this was completed. Close this window and check the ledger before trying again, so credits are not moved twice.',
  'credits.alloc.dismiss': 'Dismiss',

  // Server error codes
  'credits.error.CREDIT_WOULD_GO_NEGATIVE': 'That would take the recipient’s balance below zero.',
  'credits.error.CREDIT_LIMIT_EXCEEDED': 'That would take the recipient above its limit of {{cap}} credits.',
  'credits.error.INSUFFICIENT_CREDITS_AT_SOURCE': 'You do not have enough credits for that. Your balance may have changed; it has been refreshed.',
  'credits.error.ORGANIZATION_NOT_IN_YOUR_CHANNEL': 'That organisation is not in your channel.',
  'credits.error.CREDIT_AMOUNT_INVALID': 'That is not a valid number of credits.',
  'credits.error.generic': 'The transfer could not be completed.',
} as const;

type StripPlural<K extends string> =
  K extends `${infer B}_one` ? B : K extends `${infer B}_other` ? B : K;
/** The key the code passes: a `_one`/`_other` pair is called by its base name. */
export type CreditsKey = StripPlural<keyof typeof CREDITS_STRINGS>;

/**
 * Keys that already exist in the locale files. Listed here only so the screen
 * has an English fallback; the merger must NOT copy these.
 */
const EXISTING_STRINGS = {
  'common.retry': 'Retry',
  'common.all': 'All',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.clearFilters': 'Clear filters',
} as const;

type ExistingKey = keyof typeof EXISTING_STRINGS;

type Options = Record<string, unknown>;

/** Translate with the built-in English as the fallback. */
export function useCreditsT() {
  const { t, i18n } = useTranslation();

  const tx = useCallback(
    (key: CreditsKey, options: Options = {}): string => {
      const strings = CREDITS_STRINGS as Record<string, string>;
      const one = strings[`${key}_one`];
      const other = strings[`${key}_other`];
      if (one !== undefined || other !== undefined) {
        return t(key, {
          defaultValue_one: one ?? other,
          defaultValue_other: other ?? one,
          defaultValue: other ?? one,
          ...options,
        }) as string;
      }
      return t(key, { defaultValue: strings[key], ...options }) as string;
    },
    [t],
  );

  const tc = useCallback(
    (key: ExistingKey): string => t(key, { defaultValue: EXISTING_STRINGS[key] }) as string,
    [t],
  );

  /** A tier name, from the existing `tier.*` keys. */
  const tier = useCallback(
    (value: string): string => t(`tier.${value}`, { defaultValue: value }) as string,
    [t],
  );

  /** A reason code as a label; a code this build does not know is shown as sent. */
  const reason = (value: string): string =>
    Object.prototype.hasOwnProperty.call(CREDITS_STRINGS, `credits.reason.${value}`)
      ? tx(`credits.reason.${value}` as CreditsKey)
      : value;

  const nf = new Intl.NumberFormat(i18n.language);
  /** A number in the user's locale. Digits are tabular wherever they are shown. */
  const num = (n: number) => nf.format(n);
  /** A signed number: a true minus sign, and an explicit plus. */
  const signed = (n: number) => (n > 0 ? `+${nf.format(n)}` : n < 0 ? `−${nf.format(Math.abs(n))}` : nf.format(0));
  /** "12 credits" / "1 credit" */
  const credits = (n: number) => tx('credits.cost', { count: n, amount: nf.format(n) });

  return { t, tx, tc, tier, reason, num, signed, credits, locale: i18n.language };
}

/**
 * Turn a failed transfer into something the person can act on.
 *
 * Returns `unknownOutcome: true` when the request may have reached the server
 * and been applied — a dropped connection or a 5xx. The API has no idempotency
 * key, so blindly retrying could move the credits twice.
 */
export function useCreditsError() {
  const { tx } = useCreditsT();
  const describeGeneric = useApiError();

  return (error: unknown): { message: string; unknownOutcome: boolean } => {
    if (!(error instanceof ApiError)) {
      // fetch() itself rejected: no response, so no way to know.
      return { message: tx('credits.alloc.unknownOutcome'), unknownOutcome: true };
    }
    if (error.status >= 500) {
      return { message: tx('credits.alloc.unknownOutcome'), unknownOutcome: true };
    }
    const key = `credits.error.${error.code}` as CreditsKey;
    if (key in CREDITS_STRINGS) {
      return { message: tx(key, { ...(error.details ?? {}) }), unknownOutcome: false };
    }
    // Permission, session and other shared codes are translated elsewhere.
    return { message: describeGeneric(error), unknownOutcome: false };
  };
}

// --- Data shapes --------------------------------------------------------------

export interface LedgerEntry {
  id: string;
  organizationId: string;
  delta: number;
  reason: string;
  note: string | null;
  referenceId: string | null;
  actorId: string | null;
  actorName: string | null;
  createdAt: string;
}

export interface ChannelOrganization {
  id: string;
  name: string;
  domain: string | null;
  tier: string;
  plan: string;
  isActive: boolean;
  depth: number;
  isSelf: boolean;
  creditBalance: number;
}

export interface ChannelResponse {
  items: ChannelOrganization[];
  total: number;
  truncated: boolean;
}

/**
 * Query keys, shared so a mutation can invalidate exactly what it changed.
 *
 * Every key carries the ACTING ORGANISATION's id. The query client outlives a
 * sign-out and an organisation switch, and a balance or ledger cached for one
 * organisation must never be shown — even for a moment — while another is
 * acting. The `credits` and `organizations` prefixes are kept first so the
 * existing `invalidateQueries({ queryKey: ['credits'] })` calls elsewhere still
 * reach these.
 */
export const creditKeys = (organizationId: string) => ({
  balance: ['credits', organizationId, 'balance'] as const,
  pricing: ['credits', organizationId, 'pricing'] as const,
  ledger: ['credits', organizationId, 'ledger'] as const,
  channel: ['organizations', organizationId, 'credit-channel'] as const,
});
